import { useState } from 'react';
import { CHART_COLORS, COLORS } from '../../../constants/colors';
import { IStrategy, TTickerCurrency } from '../../../types/model';
import {
  aggregateGroups,
  computePercentage,
  getTotalValues,
} from '../../../helpers/functions';
import { CustomNumber } from '../../CustomNumber/CustomNumber';
import { ColorButton, Icon } from '../../UI';
import { ProfitLoss } from '../../ProfitLoss';
import { useThemeContext } from '../../../context/Theme';

const colors = Object.values(CHART_COLORS).map((c) => 'bg-' + c);
const colorsButton = Object.keys(COLORS);

export const StrategyComposition = ({
  width,
  strategy: {
    createdAt,
    color,
    currency,
    name,
    strategyAssets: { items: assets },
  },
}: {
  width: number;
  strategy: IStrategy;
}) => {
  const { theme } = useThemeContext();
  const [isOpenDetails, setIsOpenDetails] = useState(false);
  const [canShowAllAssets, setCanShowAllAssets] = useState(false);

  const entryValue = assets.reduce((a, b) => a + b.unit * b.entryPrice, 0);

  const {
    totalValue: totalValueStrategy,
    variations: {
      VAR_24H: dailyValue,
      VAR_7D: weeklyValue,
      VAR_30D: monthlyValue,
      VAR_1Y: YTDValue,
    },
  } = getTotalValues(
    assets,
    currency.toLocaleLowerCase() as TTickerCurrency,
    createdAt
  );

  const dailyVariation = computePercentage(dailyValue, totalValueStrategy, 100);
  const weeklyVariation = computePercentage(
    weeklyValue,
    totalValueStrategy,
    100
  );
  const monthlyVariation = computePercentage(
    monthlyValue,
    totalValueStrategy,
    100
  );
  const YTD = computePercentage(YTDValue, totalValueStrategy, 100);

  const categoris = aggregateGroups({
    assets,
    currency,
    withUnit: true,
  });

  if (width > 1) {
    return (
      <div className="border-b border-grey-10 py-2 dark:border-grey-70">
        <div className={'inline-grid grid-cols-8 p-2 w-full text-sm'}>
          <div className={'flex items-center w-full font-semibold'}>
            <ColorButton isReadOnly={true} radius={3} color={color} />
            <div className={'truncate mx-2'}>{name}</div>
          </div>

          <CustomNumber value={entryValue} displayType={'text'} />
          <CustomNumber value={totalValueStrategy} displayType={'text'} />
          <CustomNumber
            value={totalValueStrategy - entryValue}
            displayType={'text'}
          />
          <ProfitLoss
            percentage={dailyVariation}
            percentageClassName="text-sm"
          />
          <ProfitLoss
            percentage={weeklyVariation}
            percentageClassName="text-sm"
          />
          <ProfitLoss
            percentage={monthlyVariation}
            percentageClassName="text-sm"
          />
          <ProfitLoss percentage={YTD} percentageClassName="text-sm" />
        </div>

        <div className={'flex justify-end mr-10 mt-2 mb-4'}>
          <div
            className={`
                flex
                w-10/12
                relative
                self-end
                rounded-lg
                overflow-hidden
                text-center
                shadow-strong
              `}
          >
            {categoris.map(({ totalValue }, i) => (
              <div
                key={'pscp' + i}
                className={`h-3 bg-gradient-to-b ${colors[i]}`}
                style={{
                  width: `${computePercentage(
                    totalValueStrategy,
                    totalValue
                  )}%`,
                }}
              />
            ))}
            <div
              className={`
                  flex
                  w-full
                  h-3
                  left-0
                  right-0
                  absolute
                  items-center
                  rounded-full
                  opacity-70
                  bg-gradient-to-t
                  from-black
                  to-transparent
                  mix-blend-overlay
                `}
            />
          </div>
        </div>
        <div className={'flex justify-end mr-10 mb-5'}>
          <div className={'flex w-10/12 self-end text-center'}>
            {categoris.map(({ title, totalValue }, i) => (
              <div
                className={'flex mr-12 text-sm items-center gap-2'}
                key={'pscp1' + i}
              >
                <ColorButton
                  isReadOnly={true}
                  radius={3}
                  color={colorsButton[i]}
                />
                <ProfitLoss
                  className="dark:text-white"
                  displayType="text"
                  textColor={theme === 'DARK' ? 'white' : `black`}
                  usePerformance={false}
                  prefixPercentage={title + ' '}
                  percentage={computePercentage(totalValueStrategy, totalValue)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div>
        <div className={'p-2 w-full text-sm'}>
          <div className={'flex items-center w-full font-bold text-md m-2'}>
            <div
              className={`
                  bg-gradient-to-b
                  from-${color.toLowerCase()}
                  to-${color.toLowerCase()}-dark-2
                  rounded-full
                  w-3
                  h-3
                  mr-2
                `}
            />
            <div className={'font-semibold'}>{name}</div>
          </div>

          <div className={'mt-4 mx-2'}>
            <div className={'grid grid-cols-2 text-md '}>
              <span className={'text-sm text-grey-70'}>Entry Values</span>
              <span className={'text-sm text-grey-70'}>Actual</span>
            </div>
            <div className={'grid grid-cols-2 text-lg font-light mt-1'}>
              <CustomNumber
                value={entryValue}
                className={'text-md font-normal'}
                displayType={'text'}
              />
              <CustomNumber
                value={totalValueStrategy}
                className={'text-md font-normal'}
                displayType={'text'}
              />
            </div>
          </div>

          <div className={'mt-5 mx-2'}>
            <div className={'grid grid-cols-2 text-md '}>
              <span className={'text-sm text-grey-70'}>Net Change</span>
              <span className={'text-sm text-grey-70'}>% Net Change</span>
            </div>
            <div className={'grid grid-cols-2 mt-1'}>
              <ProfitLoss
                percentage={totalValueStrategy - entryValue}
                percentageClassName="text-md font-normal"
              />

              <ProfitLoss
                percentage={computePercentage(
                  entryValue,
                  totalValueStrategy - entryValue,
                  100
                )}
                percentageClassName="text-md font-normal"
              />
            </div>
          </div>
        </div>

        <div className={'flex justify-center mt-2 mb-4 mx-3'}>
          <div
            className={`
                flex
                w-full
                self-center
                rounded-lg
                overflow-hidden
                text-center
                shadow
                shadow-md
                relative
              `}
          >
            {categoris.map(({ totalValue }, i) => (
              <div
                key={'pscp' + i}
                className={`h-3 bg-gradient-to-b ${colors[i]}  `}
                style={{
                  width: `${computePercentage(
                    totalValueStrategy,
                    totalValue
                  )}%`,
                }}
              />
            ))}
            <div
              className={`
                  flex
                  w-full
                  h-3
                  left-0
                  right-0
                  absolute
                  items-center
                  rounded-full
                  opacity-70
                  bg-gradient-to-t
                  from-black
                  to-transparent
                  mix-blend-overlay
                `}
            />
          </div>
        </div>
        <div
          className={`
              grid
              grid-cols-${canShowAllAssets ? 2 : 5}
              mb-5
              mx-3
            `}
        >
          {categoris.slice(0, 2).map(({ title, totalValue }, i) => (
            <div
              className={`
                  flex
                  ${!canShowAllAssets && 'col-span-2'}
                  text-base
                  items-center
                  gap-2
                `}
              key={'pscp1' + i}
            >
              <ColorButton
                isReadOnly={true}
                radius={3}
                color={colorsButton[i]}
              />
              <ProfitLoss
                displayType="text"
                textColor="black"
                usePerformance={false}
                prefixPercentage={title + ' '}
                percentage={computePercentage(totalValueStrategy, totalValue)}
              />
            </div>
          ))}
          {categoris.length > 2 && !canShowAllAssets && (
            <div
              className="flex gap-1 ml-4 font-semibold text-md cursor-pointer"
              onClick={() => setCanShowAllAssets((pr) => !pr)}
            >
              +{categoris.length - 2}
              <div className="font-normal">more</div>
            </div>
          )}
        </div>

        {canShowAllAssets && (
          <div className={'grid grid-cols-2 mb-5 mx-3 gap-y-4'}>
            {categoris.slice(2).map(({ totalValue, title }, i) => (
              <div className={'flex text-base items-center'} key={'pscp1' + i}>
                <ColorButton
                  isReadOnly={true}
                  radius={3}
                  color={colorsButton[i]}
                />
                <ProfitLoss
                  displayType="text"
                  textColor="black"
                  usePerformance={false}
                  prefixPercentage={title + ' '}
                  percentage={computePercentage(totalValueStrategy, totalValue)}
                />
              </div>
            ))}

            <div
              className="flex items-center gap-1 ml-1 text-md cursor-pointer"
              onClick={() => setCanShowAllAssets((pr) => !pr)}
            >
              <div className="h-0.75 w-3 rounded-md mr-1 bg-black-backdrop" />
              <div className="text-sm font-semibold">Show less</div>
            </div>
          </div>
        )}

        <div
          className={'flex justify-start mx-3 mb-5 items-center'}
          onClick={() => setIsOpenDetails((pr) => !pr)}
        >
          <Icon
            svgName={isOpenDetails ? 'arrowUp' : 'arrowDown'}
            className={`
                mr-1
                h-4
                w-4
                cursor-pointer
                bg-grey-10
                p-1
                rounded-full
              `}
          />
          <div className={`flex text-sm text-grey-50 cursor-pointer`}>
            Details
          </div>
        </div>
        {isOpenDetails && (
          <div className="grid grid-cols-3 ml-3 gap-y-4">
            <div className="flex flex-col text-sm text-grey-70">
              <div>% 1D</div>
              <ProfitLoss
                percentage={dailyVariation}
                percentageClassName="text-sm mt-1"
              />
            </div>

            <div className="flex flex-col text-sm text-grey-70">
              <div>% 1W</div>
              <ProfitLoss
                percentage={weeklyVariation}
                percentageClassName="text-sm mt-1"
              />
            </div>

            <div className="flex flex-col text-sm text-grey-70">
              <div>% 1M</div>
              <ProfitLoss
                percentage={monthlyVariation}
                percentageClassName="text-sm mt-1"
              />
            </div>

            <div className="flex flex-col text-sm text-grey-70">
              <div>% YTD</div>
              <ProfitLoss percentage={YTD} percentageClassName="text-sm mt-1" />
            </div>
          </div>
        )}
        <div className={'w-full bg-grey-10 h-2 mt-4'}></div>
      </div>
    );
  }
};
